import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, Banknote } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { Card, CardBody } from "@/components/ui/Card";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { formatCurrency, formatDateTime } from "@/lib/format";
import type { WithdrawalRequest } from "@/types/wallet";

export function WithdrawalsAdminPage() {
  const [tab, setTab] = useState("requested");
  const [rejectFor, setRejectFor] = useState<WithdrawalRequest | null>(null);
  const [payFor, setPayFor] = useState<WithdrawalRequest | null>(null);
  const [reason, setReason] = useState("");
  const [payoutRef, setPayoutRef] = useState("");
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-withdrawals", tab],
    queryFn: () => get<WithdrawalRequest[]>("/wallet/wallet/withdrawals/list",
                                              tab === "all" ? {} : { status: tab }),
  });

  const approve = useMutation({
    mutationFn: (id: number) => post<WithdrawalRequest>(`/wallet/wallet/withdrawals/${id}/approve`, {}),
    onSuccess: () => {
      toast.success("Withdrawal approved");
      qc.invalidateQueries({ queryKey: ["admin-withdrawals"] });
    },
  });

  const reject = useMutation({
    mutationFn: (data: { id: number; reason: string }) =>
      post<WithdrawalRequest>(`/wallet/wallet/withdrawals/${data.id}/reject`, { reason: data.reason }),
    onSuccess: () => {
      toast.success("Withdrawal rejected and funds returned.");
      setRejectFor(null); setReason("");
      qc.invalidateQueries({ queryKey: ["admin-withdrawals"] });
    },
  });

  const pay = useMutation({
    mutationFn: (data: { id: number; payout_reference: string }) =>
      post<WithdrawalRequest>(`/wallet/wallet/withdrawals/${data.id}/pay`, { payout_reference: data.payout_reference }),
    onSuccess: () => {
      toast.success("Withdrawal marked as paid");
      setPayFor(null); setPayoutRef("");
      qc.invalidateQueries({ queryKey: ["admin-withdrawals"] });
    },
  });

  const rows = data ?? [];

  return (
    <div className="space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Withdrawals</h1>
        <p className="text-sm text-surface-500 mt-1">
          Approve → mark paid to complete. Rejection returns frozen funds back to the user's Available balance.
        </p>
      </header>

      <Card>
        <div className="px-5 pt-3 border-b border-surface-200">
          <Tabs value={tab} onChange={setTab}
                tabs={[
                  { value: "requested", label: "Requested" },
                  { value: "approved",  label: "Approved"  },
                  { value: "paid",      label: "Paid"      },
                  { value: "rejected",  label: "Rejected"  },
                  { value: "all",       label: "All"       },
                ]} />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Banknote} title="No withdrawals in this view" />
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[800px]">
                <THead>
                  <TR>
                    <TH>ID</TH><TH>User</TH>
                    <TH>Method & Account</TH> {/* 🛠️ NEW COLUMN */}
                    <TH>Amount</TH>
                    <TH>Status</TH><TH>Payout Ref</TH><TH>Created</TH><TH className="text-right">Action</TH>
                  </TR>
                </THead>
                <tbody>
                  {rows.map((w) => (
                    <TR key={w.id} className="hover:bg-surface-50 transition-colors">
                      <TD>#{w.id}</TD>
                      <TD className="font-mono text-xs">{w.user_id}</TD>
                      
                      {/* 🛠️ NEW: Show Payment Method and Account beautifully */}
                      <TD>
                        <div className="flex flex-col">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-brand-700">{w.payment_method}</span>
                          <span className="font-mono text-xs text-surface-900 font-semibold">{w.account_number || "N/A"}</span>
                        </div>
                      </TD>

                      <TD>
                        <span className="font-bold text-success-700">{formatCurrency(w.amount)}</span>
                        <div className="text-[10px] text-surface-400 mt-0.5" title={w.decision_notes}>View notes</div>
                      </TD>
                      <TD><StatusPill status={w.status} /></TD>
                      <TD className="text-2xs font-mono text-surface-500 max-w-[100px] truncate" title={w.payout_reference}>{w.payout_reference || "—"}</TD>
                      <TD className="text-2xs text-surface-500 whitespace-nowrap">{formatDateTime(w.created_at)}</TD>
                      <TD className="text-right">
                        <div className="flex justify-end gap-1">
                          {w.status === "requested" && (
                            <>
                              <Button size="sm" leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
                                      loading={approve.isPending}
                                      onClick={() => approve.mutate(w.id)}>
                                Approve
                              </Button>
                              <Button size="sm" variant="danger"
                                      leftIcon={<XCircle className="h-3.5 w-3.5" />}
                                      onClick={() => setRejectFor(w)}>
                                Reject
                              </Button>
                            </>
                          )}
                          {w.status === "approved" && (
                            <Button size="sm" onClick={() => setPayFor(w)}>
                              Mark paid
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* MODALS REMAIN THE SAME */}
      {rejectFor && (
        <Modal open onClose={() => setRejectFor(null)} title={`Reject withdrawal #${rejectFor.id}`}>
          <div className="space-y-4">
            <div className="text-sm text-surface-600 bg-surface-50 p-3 rounded-lg border border-surface-200">
              Amount to return: <strong className="text-surface-900">{formatCurrency(rejectFor.amount)}</strong>
              <div className="text-xs text-surface-500 mt-1">{rejectFor.decision_notes}</div>
            </div>
            <Input label="Rejection reason (Sent to customer)" value={reason}
                   placeholder="e.g., Invalid account number"
                   onChange={(e) => setReason(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
              <Button variant="danger" loading={reject.isPending}
                      disabled={!reason.trim()}
                      onClick={() => reject.mutate({ id: rejectFor.id, reason })}>
                Reject & Return Funds
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {payFor && (
        <Modal open onClose={() => setPayFor(null)} title={`Mark withdrawal #${payFor.id} as paid`}>
          <div className="space-y-4">
            <div className="text-sm text-surface-600 bg-surface-50 p-3 rounded-lg border border-surface-200">
              Please send <strong className="text-brand-700">{formatCurrency(payFor.amount)}</strong> to:
              <div className="mt-1 font-mono text-surface-900 font-bold uppercase">{payFor.payment_method} - {payFor.account_number}</div>
            </div>
            <Input label="Payout reference (from your bank/wallet)"
                   placeholder="e.g. TRX-12345"
                   value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPayFor(null)}>Cancel</Button>
              <Button loading={pay.isPending}
                      disabled={!payoutRef.trim()}
                      onClick={() => pay.mutate({ id: payFor.id, payout_reference: payoutRef })}>
                Confirm Paid
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}